import React, { useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import GalleryService from '../services/gallery-service';
import FileStorageService from '../services/file-storage-service';
import { toStudentUuids } from './student-uuid-helper';

export const getGalleryData = (previousData?: GalleryDto) => {
  const data: GalleryFormData = {
    formData: {
      title: previousData?.title || null,
      description: previousData?.description || null,
      exhibitionOn: previousData?.exhibitionOn || undefined,
      attachments: previousData?.files || [],
    },
    classData: [],
    studentData: [],
    unmatchedStudentUuids: [],
  };

  return data;
};

export const checkIfFirstStepWasFilled = (
  data: GalleryFormData,
  validations: object,
  allowSubmit: boolean,
  setAllowSubmit: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (!data) {
    setAllowSubmit(false);
    return;
  }

  if (
    data.formData.title &&
    data.formData.description &&
    data.formData.attachments.length > 0 &&
    Object.keys(validations).length === 0
  ) {
    setAllowSubmit(true);
  } else if (allowSubmit) {
    setAllowSubmit(false);
  }
};

export const checkIfAllStepsWereFilled = (
  data: GalleryFormData,
  validations: object,
  allowSubmit: boolean,
  setAllowSubmit: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (!data) {
    setAllowSubmit(false);
    return;
  }

  if (
    data.formData.title &&
    data.formData.description &&
    data.formData.attachments.length > 0 &&
    (data.studentData.length > 0 || data.unmatchedStudentUuids.length > 0) &&
    Object.keys(validations).length === 0
  ) {
    setAllowSubmit(true);
  } else if (allowSubmit) {
    setAllowSubmit(false);
  }
};

export const deleteAttachment = (
  fileId: string,
  setData: React.Dispatch<React.SetStateAction<GalleryFormData>>
) => {
  setData((current) => {
    const removed = current.formData.attachments.find((attachment) => attachment.fileId === fileId);
    if (removed) revokePreviewUrls([removed]);

    return {
      ...current,
      formData: {
        ...current.formData,
        attachments: current.formData.attachments.filter((attachment) => attachment.fileId !== fileId),
      },
    };
  });
};

// As miniaturas de arquivos recém-enviados são blob: URLs (ver
// FileStorageService.uploadFilesWithPartialSuccess), que prendem o arquivo
// inteiro na memória até serem revogadas — e o SPA nunca descarrega a página.
export const revokePreviewUrls = (files: GalleryFileDto[]) => {
  files.forEach((file) => {
    if (file.url?.startsWith('blob:')) URL.revokeObjectURL(file.url);
  });
};

export const useRevokePreviewUrlsOnUnmount = (data: GalleryFormData | null) => {
  const attachments = useRef<GalleryFileDto[]>([]);
  attachments.current = data?.formData.attachments ?? [];

  useEffect(() => () => revokePreviewUrls(attachments.current), []);
};

export const maxAttachments = 50;
const maxFileSizeInMb = 10;
const maxFileSize = maxFileSizeInMb * 1024 * 1024;

const showLimitModal = (setModalContent: (content: ModalContent) => any, text: string) =>
  setModalContent({
    title: 'Galeria',
    modalContent: React.createElement('p', null, text),
    submitButtonTitle: 'ok',
    submitButtonType: 'primary',
    submitData: null,
    onSubmitButtonClick: () => Promise.resolve(),
    isOpen: true,
  });

// Upload pelo drag-and-drop do ImagePreview, comum às telas de criação e edição.
export const uploadDroppedFiles = async ({
  files,
  data,
  setData,
  fileStorageService,
  setIsLoadingAttachment,
  setUploadProgress,
  setModalContent,
  setToastContent,
}: {
  files: FileList | File[];
  data: GalleryFormData;
  setData: React.Dispatch<React.SetStateAction<GalleryFormData>>;
  fileStorageService: FileStorageService;
  setIsLoadingAttachment: React.Dispatch<React.SetStateAction<boolean>>;
  setUploadProgress: React.Dispatch<React.SetStateAction<string>>;
  setModalContent: (content: ModalContent) => any;
  setToastContent: (content: ToastContent) => void;
}) => {
  const fileList = Array.from(files as ArrayLike<File>);

  if (fileList.some((file) => file.size > maxFileSize)) {
    showLimitModal(setModalContent, `O tamanho máximo de cada anexo é ${maxFileSizeInMb}MB`);
    return;
  }

  if (data.formData.attachments.length + fileList.length > maxAttachments) {
    showLimitModal(setModalContent, `Quantidade máxima de imagens é ${maxAttachments}`);
    return;
  }

  try {
    setIsLoadingAttachment(true);
    setUploadProgress('Preparando upload...');

    const result = await fileStorageService.uploadFilesWithPartialSuccess(fileList, (uploaded, total) => {
      setUploadProgress(`Enviando ${uploaded} de ${total} arquivo(s)...`);
    });

    // Atualização funcional: o upload leva tempo, e o que foi digitado no
    // formulário nesse meio-tempo não pode ser sobrescrito pelo estado antigo.
    if (result.successful.length > 0) {
      setData((current) => ({
        ...current,
        formData: {
          ...current.formData,
          attachments: current.formData.attachments.concat(result.successful),
        },
      }));
    }

    if (result.failed.length > 0) {
      const failedNames = result.failed.map((f) => f.file.name).join(', ');
      const message =
        result.successful.length > 0
          ? `${result.failed.length} arquivo(s) falharam: ${failedNames}. ${result.successful.length} arquivo(s) enviados com sucesso.`
          : `Falha ao enviar ${result.failed.length} arquivo(s): ${failedNames}`;

      setToastContent({ type: 'error', text: message, isOpen: true });
    }
  } catch (error) {
    setToastContent({
      type: 'error',
      text: (error as Error).message || 'Erro ao fazer upload dos arquivos',
      isOpen: true,
    });
  } finally {
    setUploadProgress('');
    setIsLoadingAttachment(false);
  }
};

export const handleStudentChange = (
  selectedClassrooms: number[],
  selectedStudents: number[],
  data: GalleryFormData,
  setData: React.Dispatch<React.SetStateAction<GalleryFormData>>
) => {
  if (!data) {
    return;
  }

  const newData = {
    ...data,
    formData: { ...data.formData },
    classData: selectedClassrooms,
    studentData: selectedStudents,
  };

  setData(newData);
};

export const handleFormOnChange = (
  e: React.ChangeEvent<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >,
  validations: object,
  setValidations: (value: React.SetStateAction<{}>) => any,
  setData: React.Dispatch<React.SetStateAction<GalleryFormData>>
) => {
  const id = e.target.id;
  const value = e.target.value;

  const newValidations = Object.assign({}, validations);

  // A data de exibição é opcional; os demais campos de texto são obrigatórios.
  if (!value && id !== 'exhibitionOn') newValidations[id] = 'Campo obrigatório.';
  else delete newValidations[id];

  setValidations(newValidations);
  setData((current) => ({ ...current, formData: { ...current.formData, [id]: value } }));
};

export const deleteGallery = (
  galleryService: GalleryService,
  galleryId: string,
  setModalContent: (content: ModalContent) => any,
  setToastContent: (content: ToastContent) => void,
  navigate: any
) => {
  setModalContent({
    title: 'Deletar galeria',
    modalContent: React.createElement(
      'p',
      null,
      'Deseja realmente deletar a galeria?'
    ),
    submitButtonTitle: 'confirmar deleção',
    submitButtonType: 'primary',
    submitData: null,
    onSubmitButtonClick: () =>
      galleryService
        .deleteGallery(galleryId)
        .then(() => {
          setToastContent({
            isOpen: true,
            text: 'Galeria deletada!',
            type: 'success',
          });
          navigate('/galerias/listar');
        })
        .catch(() => {
          setToastContent({
            isOpen: true,
            text: 'Houve um erro ao deletar a galeria.',
            type: 'error',
          });
        }),
    isOpen: true,
  });
};

export const buildGalleryRequest = (
  data: GalleryFormData,
  classroomList: ClassroomStudents[]
): GalleryRequest => {
  const studentUuids = toStudentUuids(data.studentData, data.unmatchedStudentUuids, classroomList);

  return {
    title: data.formData.title,
    description: data.formData.description,
    exhibitionOn: data.formData.exhibitionOn || null,
    fileIds: data.formData.attachments.map((attachment) => attachment.fileId),
    studentUuids,
  };
};

// O BFF resolve os nomes de aluno/turma/responsável no servidor, então a tabela
// de engajamento só repassa os destinatários. O engajamento de galeria tem dois
// estados (`seen`), sem a progressão visualizado -> lido dos comunicados, então
// é uma coluna de status só.
export const backofficeRecipientsToEngagementRows = (
  recipients: BackofficeGalleryRecipientDto[]
): InfosDataListRow[][] => {
  return recipients.map((recipient) => [
    { text: recipient.student.name || '—' },
    { text: recipient.classroom?.name || '—' },
    { text: recipient.responsible?.name || '—' },
    {
      text: recipient.seen ? 'Sim' : 'Não',
      customStyle: { textAlign: 'center' },
    },
  ]);
};

// exhibitionOn é uma data de calendário pura ("YYYY-MM-DD"), sem hora e sem fuso.
// parseISO do date-fns lê data pura como meia-noite LOCAL; o new Date() nativo
// leria como meia-noite UTC e mostraria o dia anterior em qualquer fuso negativo.
export const exhibitionOnToDate = (exhibitionOn?: string): Date | null => {
  return exhibitionOn ? parseISO(exhibitionOn) : null;
};

export const exhibitionOnToLabel = (exhibitionOn?: string): string | null => {
  const date = exhibitionOnToDate(exhibitionOn);

  return date ? format(date, 'dd/MM/yyyy') : null;
};

// Adapta GalleryFileDto para o formato que ImagePreview e o `imageList` do Infos
// consomem (ambos leem location/originalName). A url é assinada e some enquanto
// o upload não termina, então a key é sempre o fileId.
export const filesToImagePreviewList = (
  files: GalleryFileDto[]
): { id: string; location: string; originalName: string }[] => {
  return files.map((file) => ({
    id: file.fileId,
    location: file.url,
    originalName: file.name,
  }));
};
